/**
 * Initializes first-party analytics once per app session.
 *
 * Bootstraps PostHog with the cached user_id (if any) AFTER auth hydration
 * settles. This avoids the anon→identify merge that would otherwise fire on
 * every authenticated cold start. If there's no cached user (unauthenticated
 * first visit), PostHog initializes without a distinctID — pre-signup events
 * land under $anon_distinct_id and stitch to the real user_id on signup.
 *
 * This does not request App Tracking Transparency because Stasht analytics are
 * for product usage inside Stasht-owned surfaces, not cross-app/site tracking.
 */

import { useEffect, useState } from "react";
import { analyticsBootstrapService } from "@/services/analyticsBootstrap.service";
import { analytics } from "@/services/analytics.service";
import { useAuth } from "@/contexts/AuthContext";
import { IS_CONNECTION_SESSION } from "@/utils/connection-session";

export const AnalyticsInitializer = () => {
  const [bootstrapped, setBootstrapped] = useState(false);
  const [sessionStartFired, setSessionStartFired] = useState(false);
  const [analyticsOptInInitialized, setAnalyticsOptInInitialized] = useState(false);
  const { isLoading, user } = useAuth();

  // PostHog bootstrap: wait until AuthContext finishes hydrating so we can
  // pass the cached user_id as the initial distinctID. AuthContext.isLoading
  // flips to false once tokenManager.hydrate() + cached-user restore
  // complete. At that point `user` is either the cached user (authenticated
  // cold start) or null (anonymous).
  useEffect(() => {
    if (bootstrapped) return;
    if (isLoading) return;
    analytics.bootstrap(user?.user_id ?? null);
    setBootstrapped(true);
  }, [bootstrapped, isLoading, user?.user_id]);

  // Fire session_started once per cold start for authenticated users. NOT
  // debounced (unlike the server-side login event which is 1h-bucketed), so
  // every cold start is a datapoint. Returning-after-background sessions
  // fire a second session_started from App.tsx's appStateChange listener.
  useEffect(() => {
    if (!bootstrapped) return;
    if (sessionStartFired) return;
    if (!user?.user_id) return;
    analytics.capture("session_started", { trigger: "cold_start" });
    setSessionStartFired(true);
  }, [bootstrapped, sessionStartFired, user?.user_id]);

  // Enables first-party analytics capture once per session. Google Analytics
  // itself is route-gated in useRouteAnalytics for logged-out marketing pages.
  useEffect(() => {
    if (analyticsOptInInitialized) return;
    if (IS_CONNECTION_SESSION) return;
    analyticsBootstrapService.initializeAnalytics()
      .catch(error => {
        console.error("Error initializing analytics:", error);
      })
      .finally(() => {
        setAnalyticsOptInInitialized(true);
      });
  }, [analyticsOptInInitialized]);

  return null;
};
